import * as React from "react";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import EntityNotesButton from "../../Notes/EntityNotesButton";
import { getCriticalityIcon } from "../../../../../Shared/Utilities/criticalityIcon";
import { AlertDetailsDto } from "../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../../../messages/en-US";
import { arrowRotateCwIcon, checkIcon, xIcon } from "@progress/kendo-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { SvgIcon } from "@progress/kendo-react-common";
import { Loader } from "@progress/kendo-react-indicators";
import moment from "moment";
import { isInvalidDate } from "../../../../../Shared/Utilities/DateFormattingUtils";
import { useState } from "react";

interface Props {
  assetData: {
    alertDetails: AlertDetailsDto;
  };
  onResetAlert: (alertId: string, alert: any) => Promise<void>;
  alerts: any;
  selectedAlertType: 'critical' | 'high' | 'medium' | 'low' | null;
}

interface ActionsCellProps {
  dataItem: any;
  onResetAlert: (alertId: string, alert: any) => Promise<void>;
}

// Notes on a triggered alert — same unified entity-notes system as assets/endpoints/gateways.
// Backend doesn't serve `web/v1/notes/Alert/{id}` yet; the drawer just shows empty until it does.
const NotesCell = (props: GridCustomCellProps) => {
  const alert = props.dataItem;
  if (!alert?.id) return <td />;
  return (
    <td>
      <EntityNotesButton
        entityType="Alert"
        entityId={String(alert.id)}
        entityName={alert.name}
        entitySerial={alert.assignedTo?.name}
        headerIcon={getCriticalityIcon(alert.criticality)}
      />
    </td>
  );
};

const ActionsCell: React.FC<ActionsCellProps> = ({ dataItem, onResetAlert }) => {
  const localizationService = useLocalization();

  return (
    <td>
      <Tooltip content={() => localizationService.toLanguageString('custom.resetAlert', enMessages.custom.resetAlert)} position="bottom">
        <Button
          className="ml-1"
          svgIcon={arrowRotateCwIcon}
          themeColor="primary"
          disabled={!dataItem.alertStatus?.isTriggered}
          onClick={() => onResetAlert(dataItem.id, dataItem)}
          title={localizationService.toLanguageString('custom.reset', enMessages.custom.reset)}
        />
      </Tooltip>
    </td>
  );
};

export const AlertsTableComponent: React.FC<Props> = ({ assetData, onResetAlert, alerts, selectedAlertType }) => {
  const localizationService = useLocalization();
  const [isResetting, setIsResetting] = useState(false);

  const handleResetAlert = async (alertId: string, alert: any) => {
    if (isResetting) return;
    
    setIsResetting(true);
    try {
      await onResetAlert(alertId, alert);
    } finally {
      setIsResetting(false);
    }
  };

  const TriggeredCell = (props: any) => {
    const { dataItem } = props;
    const alertStatus = dataItem.alertStatus;

    const formatDate = (dateString: string) => {
      if (!dateString || isInvalidDate(dateString)) return "-";
      return moment(dateString).format("DD/MM/YYYY @ HH:mm:ss");
    };

    const lastEvaluated = formatDate(alertStatus?.lastEvaluated);
    const lastNotified = formatDate(alertStatus?.lastNotified);
    const lastReset = formatDate(alertStatus?.lastReset);
    const lastTriggered = formatDate(alertStatus?.lastTriggered);

    const TooltipContentTemplate = () => (
      <div className="flex flex-col gap-1">
        <div>
          {localizationService.toLanguageString("custom.lastEvaluated", enMessages.custom.lastEvaluated)}: {lastEvaluated}
        </div>
        <div>
          {localizationService.toLanguageString("custom.lastNotified", enMessages.custom.lastNotified)}: {lastNotified}
        </div>
        <div>
          {localizationService.toLanguageString("custom.lastReset", enMessages.custom.lastReset)}: {lastReset}
        </div>
        <div>
          {localizationService.toLanguageString("custom.lastTriggered", enMessages.custom.lastTriggered)}: {lastTriggered}
        </div>
      </div>
    );

    return (
      <td>
        <Tooltip
          position="bottom"
          anchorElement="target"
          content={TooltipContentTemplate}
          parentTitle={true}
        >
          <span 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '26px',
              height: '26px'
            }}
            title={localizationService.toLanguageString("custom.triggered", enMessages.custom.triggered)}
          >
            <SvgIcon 
              icon={alertStatus?.isTriggered ? checkIcon : xIcon} 
              size="large"
            />
          </span>
        </Tooltip>
      </td>
    );
  };

  const AssignedToCell = (props) => {
    return (
      <td>
        <div>{props.dataItem.assignedTo.name}</div>
        <div className="text-xs">{props.dataItem.assignedTo.details}</div>
      </td>
    );
  }

  return (
    <div className="relative">
      {isResetting && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-2">
            <Loader size="large" />
          </div>
        </div>
      )}
      <Grid
        data={alerts || []}
        style={{ maxHeight: '350px' }}
        scrollable="scrollable"
      >
        <GridColumn
          field="name"
          title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
        />
        <GridColumn
          field="assignedToName"
          title={localizationService.toLanguageString('custom.assignedTo', enMessages.custom.assignedTo)}
          cells={{ data: AssignedToCell }}
        />
        <GridColumn
          field="type"
          title={localizationService.toLanguageString('custom.type', enMessages.custom.type)}
        />
        <GridColumn
          field="triggered"
          title={localizationService.toLanguageString('custom.triggered', enMessages.custom.triggered)}
          cells={{ data: TriggeredCell }}
          width={100}
        />
        <GridColumn
          field="actions"
          title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
          cells={{
            data: (props) => <ActionsCell {...props} onResetAlert={handleResetAlert} />
          }}
          width={90}
          filterable={false}
          sortable={false}
        />
        <GridColumn
          field="notes"
          title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}
          cells={{ data: NotesCell }}
          width={65}
          filterable={false}
          sortable={false}
        />
      </Grid>
    </div>
  );
}; 